import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import {
  Action,
  FileInput,
  Modal,
  SupportingText,
  SwitchInput,
} from 'corso-admin-ui';
import { FormEventHandler } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import api from '~/api';
import { useInvalidateProduct } from '~/features/products/ProductForm';
import { useToast } from '~/hooks/useToast';
import { useStoreId } from '~/providers/StoreUserProvider';

const missingFileError = 'Choose a CSV to upload.';

const uploadProductsFormSchema = z.object({
  file: z
    .instanceof(FileList, { error: missingFileError })
    .transform((files) => files.item(0))
    .pipe(z.instanceof(File, { error: missingFileError })),
  shouldDedupeBySku: z.boolean(),
});

export default function UploadProducts({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const storeId = useStoreId();
  const invalidateProduct = useInvalidateProduct();
  const { show: showToast } = useToast();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(uploadProductsFormSchema),
    defaultValues: { shouldDedupeBySku: true },
  });

  const closeAndReset = () => {
    onClose();
    reset();
  };

  const {
    mutate: importProducts,
    isPending,
    isError,
  } = useMutation({
    mutationKey: ['product', 'import', storeId],
    mutationFn: async ({
      file,
      shouldDedupeBySku,
    }: z.output<typeof uploadProductsFormSchema>) =>
      api
        .store(storeId)
        .product.import({ csv: await file.text(), shouldDedupeBySku }),
    onSuccess: () => {
      closeAndReset();
      showToast("Upload started. We'll send a notification when it's done.");
      return invalidateProduct();
    },
  });
  const formId = 'upload-products-form';

  const onSubmit: FormEventHandler = (event) => {
    handleSubmit((values) => importProducts(values))(event).catch(
      console.error,
    );
  };

  return (
    <Modal
      show={show}
      title="Upload Products"
      onClose={closeAndReset}
      actions={
        <>
          <Action onClick={closeAndReset}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            form={formId}
            disabled={isPending}
            loading={isPending}
          >
            Upload
          </Action>
        </>
      }
    >
      <form id={formId} onSubmit={onSubmit} className="flex flex-col gap-4">
        <FileInput
          id="upload-products-file"
          label="Product CSV"
          details="A CSV in the Shopify product export format."
          accept=".csv,text/csv"
          visuallyRequired
          error={errors.file?.message}
          {...register('file')}
        />
        <Action to="/sample-off-platform-products.csv" download>
          Download Sample CSV
        </Action>
        <Controller
          control={control}
          name="shouldDedupeBySku"
          render={({ field: { onChange, value } }) => (
            <SwitchInput
              id="upload-products-dedupe-by-sku"
              label="Skip Existing Shopify SKUs"
              details="Rows whose SKU already belongs to a product synced from Shopify are skipped."
              checked={value}
              onChange={onChange}
            />
          )}
        />
        {isError && (
          <SupportingText error>
            Sorry, failed to upload the products. Please try again.
          </SupportingText>
        )}
      </form>
    </Modal>
  );
}
